/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import FolderTenderHammerNoColor from '~src/assets/nav-icons/folder-tender-hammer-no-color.svg';
import IconButton from '~components/_common/IconButton/IconButton';
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Menu,
  message,
  Modal,
  Popover,
  Radio,
  Tooltip,
  Upload,
  UploadProps,
} from 'antd';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useHistory } from 'react-router-dom';
import { useStyles } from '~src/hooks/useStyles';
import { DownOutlined } from '@ant-design/icons';
import { TenderFolderModal } from '~components/popups-modals/TenderFolderModal/TenderFolderModal';
import { styleFn } from '../../TenderTabs.styles';
import { TenderTabKeys } from '../../TenderTabs';
import Icon from '~components/_common/AppIcon/Icon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStore } from '~src/store';
import { InviteModal } from '~components/popups-modals/InviteModal/InviteModal.modal';
import {
  TenderCoreRegVM,
  TenderRegistrationVM,
  UI_SHARE_KEY_STATUS,
} from '~src/types/db.d.ts';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTenderSharesList } from '~src/hooks/api-hooks/useTenderSharesList';
import { CorrelationLetterModal } from '~components/popups-modals/MovementsModals/CorrelationLetter.modal';
import { queryCache, useMutation, useQuery } from 'react-query';
import {
  createInstantMessageDraft,
  InstantMessage,
  updateInstantMessageDraft,
} from '~src/services/api/movements';
import { activateSecondaryKeys } from '~src/services/api/secondaryVendors';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { useInstantMessageDrafts } from '~src/hooks/api-hooks/useInstantMessageDrafts';
import { MESSAGE_TYPE_CONFIG } from '~src/utils/messageTypeUtils';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import {
  getPublicShareStatus,
  publicShareStatusIntlId,
} from '~components/pages/PublicShare/PublicShare.util';
import { getUiShareKeyStatus } from '~src/utils/shareUtils/useShareKeysStatusUtils';

const DEFAULT_STATES: readonly DB_STATE[] = [
  DB_STATE.DRAFT,
  DB_STATE.AVAILABLE,
  DB_STATE.ACCEPTED,
  DB_STATE.REJECTED,
];

const WITH_REMOVED_STATES_LIST: readonly DB_STATE[] = DEFAULT_STATES.concat([
  DB_STATE.REMOVED,
]);

export type TenderActionsProps = {
  projectId?: number;
  tenderId: string;
  showDeleted: boolean;
  setShowDeleted: Dispatch<SetStateAction<boolean>>;
  setShareTenderModal: Dispatch<SetStateAction<boolean>>;
  setTenderOptionsModal: Dispatch<SetStateAction<boolean>>;
  handleSharesToPrint: () => void;
  handleDocumentsToPrint: () => void;
  handleMovementsToPrint: () => void;
  handleContractToPrint: () => void;
  handleOfferToPrint: () => void;
  handleUploadChange: UploadProps['onChange'];
  tenderData: TenderRegistrationVM | TenderCoreRegVM;
  tenderFolderModalState: boolean;
  setTenderFolderModalState: Dispatch<SetStateAction<boolean>>;
  currentPermissionLevel: RIGHTS_LEVEL;
};

const TenderActions: React.FC<TenderActionsProps> = ({
  projectId,
  tenderId,
  showDeleted,
  setShowDeleted,
  setShareTenderModal,
  setTenderOptionsModal,
  handleSharesToPrint,
  handleDocumentsToPrint,
  handleMovementsToPrint,
  handleContractToPrint,
  handleOfferToPrint,
  handleUploadChange,
  tenderData,
  tenderFolderModalState,
  setTenderFolderModalState,
  currentPermissionLevel,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();
  const tenderIsCompleted = tenderData?.state === 6;
  const isTenderAdmin = currentPermissionLevel >= RIGHTS_LEVEL.WRITE;
  const publicShareStatus = getPublicShareStatus(tenderData?.public);

  const publicShareStatusColor = {
    private: '#8c8c8c',
    publicated: '#52c41a',
    revoked: '#ff4d4f',
  }[publicShareStatus];

  const {
    addToFilter,
    removeFromFilter,
    filterStates,
    setAllSelected,
    removeAllSelected,
    shareKeysInviteFilter,
    setShareKeysInviteFilter,
  } = useStore();

  const isOnTenderSharesPage =
    history.location.pathname.split('/').pop() === TenderTabKeys.shares;
  const isOnTenderDocumentsFirstLevelPage =
    history.location.pathname.split('/').pop() === 'documents';
  const isOnTenderDetails =
    history.location.pathname.split('/').pop() === 'details';
  const isOnTenderMovements =
    history.location.pathname.split('/').pop() === 'movements';
  const isOnTenderContract =
    history.location.pathname.split('/').pop() === 'contract';
  const isOnTenderOffers =
    history.location.pathname.split('/').pop() === 'offers';
  const isOnTenderQA =
    history.location.pathname.split('/').pop() === 'questions';
  const isOnTenderDocumentsNextLevelPage = history.location.pathname
    .split('/')
    .includes('tender-folders');
  const isOnTenderDocuments =
    isOnTenderDocumentsFirstLevelPage || isOnTenderDocumentsNextLevelPage;

  const handleTenderFolderModalOpen = (state: boolean, modal: string) => {
    switch (modal) {
      case 'tender-folder':
        setTenderFolderModalState(state);
        break;
      case 'invite-modal':
        setShowInviteModal(state);
        break;
    }
  };

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showStopDisclaimerModal, setShowStopDisclaimerModal] = useState(false);
  const [
    showActivateSecondaryKeysModal,
    setShowActivateSecondaryKeysModal,
  ] = useState(false);
  const [visibleCorrectionLetter, setVisibleCorrectionLetter] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<InstantMessage | null>(
    null
  );
  const [isCorrectionMode, setIsCorrectionMode] = useState(false);

  const {
    data: instantMessageDraftsData,
    refetch: refetchDrafts,
  } = useInstantMessageDrafts({
    projectId,
    tenderId,
    enabled: isOnTenderQA || isOnTenderMovements,
  });

  const draftCount = Array.isArray(instantMessageDraftsData)
    ? instantMessageDraftsData.length
    : 0;

  const [createDraft, { isLoading: isCreatingDraft }] = useMutation(
    createInstantMessageDraft,
    {
      onSuccess: async (data) => {
        const draftId = data.instant_message_draft_id;
        const defaultRef = isOnTenderOffers ? 'RESULT' : 'GEN';

        const newDraft: InstantMessage = {
          id: draftId,
          title: '',
          message: '',
          created_by_full_name: '',
          created_at: Date.now(),
          attachments: [],
          reference: defaultRef,
        };

        try {
          await updateInstantMessageDraft({
            projectId,
            tenderId: Number(tenderId),
            draftId: draftId,
            body: { title: '', message: '', reference: defaultRef },
          });

          message.success(intl.formatMessage({ id: 'draft-created-success' }));
          queryCache.invalidateQueries([
            'instant_message_drafts',
            projectId,
            tenderId,
          ]);

          handleOpenInstantMessage(newDraft);
        } catch (e) {
          handleOpenInstantMessage(newDraft);
        }
      },
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'draft-create-failed' }) ||
            'Failed to create draft'
        );
      },
    }
  );

  const handleCreateNewDraft = () => {
    if (projectId && tenderId) {
      createDraft({ projectId, tenderId: Number(tenderId) });
    }
  };

  const handleOpenInstantMessage = (draft: InstantMessage | null) => {
    setIsCorrectionMode(false);
    setSelectedDraft(draft);
    setVisibleCorrectionLetter(true);
  };

  const handleOpenCorrectionLetter = () => {
    setIsCorrectionMode(true);
    setSelectedDraft(null);
    setVisibleCorrectionLetter(true);
  };

  const shareTendersQuery = useTenderSharesList(
    {
      projectId,
      tenderId,
      states: WITH_REMOVED_STATES_LIST.map((value) => value),
    },
    {}
  );

  const shareCount = Array.isArray(shareTendersQuery.data)
    ? shareTendersQuery.data.length
    : 0;

  // Confirmed via GET /projects/{project_id}/tenders/{tender_id} - the flag
  // lives on the tender/share object itself (tenderData), not on individual
  // share_keys list items.
  const isSecondaryKeysOn = Boolean(tenderData?.share_is_secondary_key_on);

  const [
    doActivateSecondaryKeys,
    { isLoading: isActivatingSecondaryKeys },
  ] = useMutation(activateSecondaryKeys, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
      queryCache.invalidateQueries([QueryKeys.tenderRegistration]);
      setShowActivateSecondaryKeysModal(false);
    },
    onError: () => {
      message.error(intl.formatMessage({ id: 'tender-share-update-error' }));
    },
  });

  const handleActivateSecondaryKeys = () => {
    if (projectId && tenderId) {
      doActivateSecondaryKeys({ projectId, tenderId });
    }
  };

  const statusCounts = useMemo(() => {
    if (!Array.isArray(shareTendersQuery.data))
      return {} as Record<number, number>;
    return shareTendersQuery.data.reduce((acc, share) => {
      const s = getUiShareKeyStatus(share);
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);
  }, [shareTendersQuery.data]);

  const moreMenu = (
    <Menu>
      <Menu.Item key="showDeleted">
        <Checkbox
          checked={showDeleted}
          onChange={(e) => setShowDeleted(e.target.checked)}>
          {intl.formatMessage({ id: 'show-deleted' })}
        </Checkbox>
      </Menu.Item>
      {/* DISABLED BY JIRA: TV1-176 disable all print features */}
      {/*<Menu.Item onClick={handleDocumentsToPrint} key="printPage">*/}
      {/* <IconButton*/}
      {/* iconStyle="far"*/}
      {/* icon="print"*/}
      {/* customStyle={{*/}
      {/* fontSize: '20px',*/}
      {/* transition: '0.3s',*/}
      {/* }}*/}
      {/* backgroundOff*/}
      {/* />*/}
      {/* Print page*/}
      {/*</Menu.Item>*/}
      {/* Add more Menu.Items as needed */}
    </Menu>
  );

  const messageMenu = (
    <Menu>
      <Menu.Item key="correction-letter" onClick={handleOpenCorrectionLetter}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon icon={['far', 'plus']} css={{ color: '#51ac08' }} />
          <span>
            {intl.formatMessage({ id: 'movement-correction-letter' }) ||
              'Correction Letter'}
          </span>
        </div>
      </Menu.Item>

      <Menu.Item
        key="new"
        disabled={isCreatingDraft}
        onClick={handleCreateNewDraft}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon icon={['far', 'plus']} css={{ color: '#51ac08' }} />
          <span>
            {intl.formatMessage({ id: 'create-new-draft' }) ||
              'Create New Draft'}
          </span>
        </div>
      </Menu.Item>

      <Menu.Divider />

      {Array.isArray(instantMessageDraftsData) &&
        instantMessageDraftsData.map((draft) => {
          const typeConfig = MESSAGE_TYPE_CONFIG.find(
            (c) => c.key === draft.reference
          );
          return (
            <Menu.Item
              key={draft.id}
              onClick={() => handleOpenInstantMessage(draft)}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    color: typeConfig?.color || '#FF8C00',
                    display: 'flex',
                    fontSize: '14px',
                  }}>
                  {typeConfig?.icon || <Icon icon={['far', 'file-edit']} />}
                </span>
                <span
                  style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '120px',
                  }}>
                  {draft.title ||
                    `${intl.formatMessage({
                      id: 'messages-btn-drafts-title',
                    })} ${draft.serial_number}`}
                </span>
              </div>
            </Menu.Item>
          );
        })}

      {(!instantMessageDraftsData || instantMessageDraftsData.length === 0) && (
        <Menu.Item disabled key="no-drafts">
          <span style={{ color: '#999', fontSize: '12px' }}>
            {intl.formatMessage({ id: 'no-drafts' }) || 'No drafts'}
          </span>
        </Menu.Item>
      )}
    </Menu>
  );

  const handleCheckboxChange = (id: number, checked: boolean) => {
    checked ? addToFilter(id) : removeFromFilter(id);
  };

  const content = (
    <div css={styles.filterWrapper}>
      {/* Primary-/Secondary Share Keys: orthogonal to the state checkboxes
          below - only meaningful (and only shown) once the feature is
          active for this tender, since has_secondary_share_keys otherwise
          never varies. */}
      {isSecondaryKeysOn && (
        <Fragment>
          <Radio.Group
            value={shareKeysInviteFilter}
            onChange={(e) => setShareKeysInviteFilter(e.target.value)}
            css={styles.filterWrapper}>
            <Radio value="all">
              {intl.formatMessage({ id: 'secondary-keys-filter-show-all' })}
            </Radio>
            <Radio value="withSecondaryInvites">
              {intl.formatMessage({
                id: 'secondary-keys-filter-show-with-invites',
              })}
            </Radio>
          </Radio.Group>
          <Divider css={{ margin: '4px 0' }} />
        </Fragment>
      )}
      <div css={styles.filterItemWrapper}>
        <div css={styles.filterIconWrapper}>
          {intl.formatMessage({ id: 'show-all' })}
        </div>
        <Checkbox
          onChange={(e) =>
            e.target.checked ? setAllSelected() : removeAllSelected()
          }
          checked={filterStates.length === 6}
        />
      </div>

      {[
        {
          id: UI_SHARE_KEY_STATUS.AWAITING,
          label: `${intl.formatMessage({
            id: 'tender-share-key-state-awaiting',
          })}`,
          color: 'grey',
          icon: 'stopwatch',
        },
        {
          id: UI_SHARE_KEY_STATUS.INVITED,
          label: `${intl.formatMessage({
            id: 'tender-share-key-state-invited',
          })}`,
          color: 'orange',
          icon: 'envelope',
        },
        {
          id: UI_SHARE_KEY_STATUS.OPENED,
          label: `${intl.formatMessage({
            id: 'tender-share-key-state-opened',
          })}`,
          color: 'orange',
          icon: 'envelope-open-text',
        },
        {
          id: UI_SHARE_KEY_STATUS.CLAIMED,
          label: `${intl.formatMessage({
            id: 'tender-share-key-state-claimed',
          })}`,
          color: 'green',
          icon: 'check-circle',
        },
        {
          id: UI_SHARE_KEY_STATUS.REJECTED,
          label: `${intl.formatMessage({
            id: 'tender-share-key-state-rejected',
          })}`,
          color: 'red',
          icon: 'ban',
        },
        {
          id: UI_SHARE_KEY_STATUS.DELETED,
          label: `${intl.formatMessage({
            id: 'tender-share-key-state-deleted',
          })}`,
          color: 'purple',
          icon: 'trash',
        },
      ].map(({ id, label, color, icon }) => (
        <div css={styles.filterItemWrapper} key={id}>
          <div css={styles.filterIconWrapper}>
            <FontAwesomeIcon
              icon={[label === 'ACCEPTED' ? 'fad' : 'far', icon]}
              style={{ color }}
            />
            {`${label} (${statusCounts[id] ?? 0})`}
          </div>
          <Checkbox
            checked={filterStates.includes(id)}
            onChange={(e) => handleCheckboxChange(id, e.target.checked)}
          />
        </div>
      ))}
    </div>
  );

  return (
    <Fragment>
      {/* Not implemented on other pages */}
      {!isOnTenderSharesPage && !isOnTenderDocumentsFirstLevelPage && (
        /*     window.sessionStorage.getItem('appState') !== 'timeMachine' &&
                    window.sessionStorage.getItem('appState') !== 'superUser' && */ <Fragment>
          {' '}
          {/* <div
            css={styles.buttonDiv}
            onClick={() => message.error('Feature not implemented')}>
            <IconButton
              iconStyle="far"
              icon="pen"
              customStyle={styles.iconsHeader}
              backgroundOff
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {intl.formatMessage({ id: 'edit' })}
            </p>
          </div>
          <div
            css={styles.buttonDiv}
            onClick={() => message.error('Feature not implemented')}>
            <IconButton
              iconStyle="far"
              icon="clone"
              customStyle={styles.iconsHeader}
              backgroundOff
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {intl.formatMessage({ id: 'duplicate' })}
            </p>
          </div>
          <div css={styles.buttonDiv} onClick={() => setShareTenderModal(true)}>
            <IconButton
              iconStyle="far"
              icon="share-from-square"
              customStyle={styles.iconsHeader}
              backgroundOff
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {intl.formatMessage({ id: 'share' })}
            </p>
          </div>
          <div
            css={styles.buttonDiv}
            onClick={() => message.error('Feature not implemented')}>
            <IconButton
              iconStyle="far"
              icon="paper-plane"
              customStyle={styles.iconsHeader}
              backgroundOff
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {intl.formatMessage({ id: 'release' })}
            </p>
          </div> */}
        </Fragment>
      )}
      {/*   {isOnTenderDetails && (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                onClick={() =>
                  handleTenderFolderModalOpen(true, 'tender-folder')
                }
                key="1"
                icon={
                  <div css={styles.tenderActionBtnIcon}>
                    <Icon icon={['far', 'pen']} />
                  </div>
                }>
                {intl.formatMessage({ id: 'tender-folders' })}
              </Menu.Item>
              <Menu.Item
                key="2"
                icon={
                  <div css={styles.tenderActionBtnIcon}>
                    <Icon icon={['far', 'clone']} />
                  </div>
                }>
                {intl.formatMessage({ id: 'duplicate' })}
              </Menu.Item>
              <Menu.Item
                key="3"
                icon={
                  <div css={styles.tenderActionBtnIcon}>
                    <Icon icon={['far', 'share-from-square']} />
                  </div>
                }>
                {intl.formatMessage({ id: 'share' })}
              </Menu.Item>
              <Menu.Item
                key="4"
                icon={
                  <div css={styles.tenderActionBtnIcon}>
                    <Icon icon={['far', 'paper-plane']} />
                  </div>
                }
                onClick={() => setShareTenderModal(true)}>
                {intl.formatMessage({ id: 'release' })}
              </Menu.Item>
              <Menu.Item
                key="5"
                icon={
                  <div css={styles.tenderActionBtnIcon}>
                    <Icon icon={['far', 'times-circle']} />
                  </div>
                }
                onClick={() => setShowStopDisclaimerModal(true)}>
                {intl.formatMessage({ id: 'Stop' })}
              </Menu.Item>
              <Menu.Item
                key="6"
                icon={
                  <div css={styles.tenderActionBtnIcon}>
                    <Icon icon={['far', 'cog']} />
                  </div>
                }
                onClick={() => setTenderOptionsModal(true)}>
                {intl.formatMessage({ id: 'tender-options' })}
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}>
          <div css={styles.buttonDiv}>
            <IconButton
              iconStyle="fas"
              icon="list-ul"
              customStyle={styles.iconsHeader}
              backgroundOff
            />
            <p css={{ marginBottom: 0, marginLeft: 2 }}>
              {intl.formatMessage({ id: 'options' })}{' '}
              <DownOutlined as="span" rev="example" />{' '}
            </p>
          </div>
        </Dropdown>
      )} */}
      {isOnTenderDetails && (
        <Tooltip
          title={intl.formatMessage({
            id: publicShareStatusIntlId[publicShareStatus],
          })}
          placement="bottom">
          <Button
            type="text"
            onClick={() => setTenderOptionsModal(true)}
            css={{
              '&&, &&:hover, &&:focus, &&:active': {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '30px',
                padding: '0 12px',
                background: '#fff',
                border: '1px solid #d9d9d9',
                borderBottom: `2px solid ${publicShareStatusColor}`,
                borderRadius: '4px',
                boxShadow: 'none',
                color: '#262626',
                transform: 'none',
                boxSizing: 'border-box',
              },

              '&&:hover, &&:focus': {
                background: '#fafafa',
                borderColor: '#bfbfbf',
                borderBottomColor: publicShareStatusColor,
              },
            }}>
            <FontAwesomeIcon
              icon={['far', 'cog']}
              style={{
                marginRight: 8,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                marginRight: 8,
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}>
              {intl.formatMessage({ id: 'tender-options' })}
            </span>

            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                minWidth: 8,
                borderRadius: '50%',
                backgroundColor: publicShareStatusColor,
                flexShrink: 0,
              }}
            />
          </Button>
        </Tooltip>
      )}
      {isOnTenderSharesPage && (
        <Fragment>
          <Tooltip
            title={
              tenderIsCompleted &&
              intl.formatMessage({ id: 'tender-data-completed' })
            }
            placement={'bottom'}>
            <Button
              disabled={tenderIsCompleted}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              type={'text'}
              onClick={() => {
                if (shareCount < 1) {
                  setShowDisclaimerModal(true);
                } else {
                  setShowInviteModal(true);
                }
              }}>
              <IconButton
                iconStyle="far"
                icon="paper-plane"
                customStyle={styles.iconsHeader}
                backgroundOff
                disabled={tenderIsCompleted}
              />
              {intl.formatMessage({ id: 'invite' })}
            </Button>
          </Tooltip>
          <Popover placement="bottomRight" content={content} trigger="click">
            <div css={styles.buttonDiv}>
              <IconButton
                iconStyle="far"
                icon="filter-list"
                customStyle={styles.iconsHeader}
                backgroundOff
              />
              <p css={{ marginBottom: 0, marginLeft: 2 }}>
                {intl.formatMessage({ id: 'filter' })}
              </p>
            </div>
          </Popover>
          {/* Primary-/Secondary Share Keys: activation control - placed last
              (far right) in the actions row, after Filter. */}
          {isSecondaryKeysOn ? (
            <Tooltip
              title={intl.formatMessage({
                id: 'secondary-keys-enabled-tooltip',
              })}
              placement={'bottom'}>
              <Button
                disabled
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '50px',
                }}
                type={'text'}>
                <IconButton
                  iconStyle="far"
                  icon="user-plus"
                  customStyle={styles.iconsHeader}
                  backgroundOff
                  disabled
                />
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                tenderIsCompleted
                  ? intl.formatMessage({ id: 'tender-data-completed' })
                  : shareCount < 1
                  ? intl.formatMessage({
                      id: 'secondary-keys-enable-disabled-no-vendors',
                    })
                  : undefined
              }
              placement={'bottom'}>
              <Button
                disabled={tenderIsCompleted || shareCount < 1}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
                type={'text'}
                onClick={() => setShowActivateSecondaryKeysModal(true)}>
                <IconButton
                  iconStyle="far"
                  icon="user-plus"
                  customStyle={styles.iconsHeader}
                  backgroundOff
                  disabled={tenderIsCompleted || shareCount < 1}
                />
                {intl.formatMessage({ id: 'secondary-keys-enable' })}
              </Button>
            </Tooltip>
          )}
          {/*<div*/}
          {/*  css={styles.buttonDiv}*/}
          {/*  onClick={() => message.error('Feature not implemented')}>*/}
          {/*  <IconButton*/}
          {/*    iconStyle="far"*/}
          {/*    icon="plus-circle"*/}
          {/*    customStyle={styles.iconsHeader}*/}
          {/*    backgroundOff*/}
          {/*  />*/}
          {/*  <p css={{ marginBottom: 0, marginLeft: 2 }}>*/}
          {/*    {intl.formatMessage({ id: 'add' })}*/}
          {/*  </p>*/}
          {/*</div>*/}
          {/*<div css={styles.buttonDiv} onClick={handleSharesToPrint}>*/}
          {/*  <IconButton*/}
          {/*    iconStyle="far"*/}
          {/*    icon="print"*/}
          {/*    customStyle={styles.iconsHeader}*/}
          {/*    backgroundOff*/}
          {/*  />*/}
          {/*  <p css={{ marginBottom: 0, marginLeft: 2 }}>*/}
          {/*    {intl.formatMessage({ id: 'print-page' })}*/}
          {/*  </p>*/}
          {/*</div>*/}
        </Fragment>
      )}
      {isOnTenderContract && (
        <Fragment>
          {/* DISABLED BY JIRA: TV1-176 disable all print features */}
          {/*<div*/}
          {/*  css={styles.buttonDiv}*/}
          {/*  onClick={handleContractToPrint}*/}
          {/*  key="printPage">*/}
          {/*  <IconButton*/}
          {/*    iconStyle="far"*/}
          {/*    icon="print"*/}
          {/*    customStyle={{*/}
          {/*      fontSize: '20px',*/}
          {/*      transition: '0.3s',*/}
          {/*    }}*/}
          {/*    backgroundOff*/}
          {/*  />*/}
          {/*  {intl.formatMessage({ id: 'print-page' })}*/}
          {/*</div>*/}
        </Fragment>
      )}
      {isOnTenderOffers && (
        <Fragment>
          {/* DISABLED BY JIRA: TV1-176 disable all print features */}
          {/*<div*/}
          {/*  css={styles.buttonDiv}*/}
          {/*  onClick={handleOfferToPrint}*/}
          {/*  key="printPage">*/}
          {/*  <IconButton*/}
          {/*    iconStyle="far"*/}
          {/*    icon="print"*/}
          {/*    customStyle={{*/}
          {/*      fontSize: '20px',*/}
          {/*      transition: '0.3s',*/}
          {/*    }}*/}
          {/*    backgroundOff*/}
          {/*  />*/}
          {/*  {intl.formatMessage({ id: 'print-page' })}*/}
          {/*</div>*/}
        </Fragment>
      )}
      {isOnTenderDocuments && (
        <Fragment>
          {/* Document actions (edit, upload, etc.) */}
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  onClick={() =>
                    handleTenderFolderModalOpen(true, 'tender-folder')
                  }
                  key="1"
                  icon={
                    <NavIcon
                      customStyle={{
                        width: '26px',
                        height: '16px',
                        marginRight: '4px',
                      }}
                      icon={FolderTenderHammerNoColor}
                    />
                  }>
                  {intl.formatMessage({
                    id: 'tender-documents-edit-tender-folders-btn',
                  })}
                </Menu.Item>
                <Menu.Item
                  key="2"
                  icon={
                    <Upload
                      multiple
                      beforeUpload={() => false}
                      onChange={handleUploadChange}>
                      <div>
                        <IconButton
                          icon={['far', 'file-upload']}
                          backgroundOff
                        />
                        {intl.formatMessage({
                          id: 'tender-documents-edit-tender-attachments-btn',
                        })}
                      </div>
                    </Upload>
                  }
                />
              </Menu>
            }
            trigger={['click']}
            disabled={isOnTenderDocumentsNextLevelPage} // disable deeper folders
          >
            <Tooltip
              title={intl.formatMessage({ id: 'go-back-docs-root' })}
              placement="bottom"
              open={isOnTenderDocumentsNextLevelPage ? undefined : false} // show only when disabled
              trigger={isOnTenderDocumentsNextLevelPage ? ['hover'] : []} // disable hover on root
            >
              <div
                css={styles.buttonDiv}
                style={
                  isOnTenderDocumentsNextLevelPage
                    ? { opacity: 0.5, cursor: 'not-allowed' }
                    : undefined
                }>
                <IconButton
                  iconStyle="far"
                  icon="pen"
                  customStyle={styles.iconsHeader}
                  backgroundOff
                />
                <p css={{ marginBottom: 0, marginLeft: 2 }}>
                  {intl.formatMessage({ id: 'edit-documents' })}{' '}
                  <DownOutlined as="span" rev="example" />
                </p>
              </div>
            </Tooltip>
          </Dropdown>

          <Dropdown
            overlay={moreMenu}
            trigger={['click']}
            disabled={isOnTenderDocumentsNextLevelPage}>
            <Tooltip
              title={intl.formatMessage({ id: 'go-back-docs-root' })}
              placement="bottom"
              open={isOnTenderDocumentsNextLevelPage ? undefined : false}
              trigger={isOnTenderDocumentsNextLevelPage ? ['hover'] : []}>
              <div
                css={styles.buttonDiv}
                style={
                  isOnTenderDocumentsNextLevelPage
                    ? { opacity: 0.5, cursor: 'not-allowed' }
                    : undefined
                }>
                <IconButton
                  iconStyle="far"
                  icon="ellipsis-h"
                  customStyle={styles.iconsHeader}
                  backgroundOff
                />
                <p css={{ marginBottom: 0, marginLeft: 2 }}>
                  {intl.formatMessage({ id: 'more' })}
                </p>
              </div>
            </Tooltip>
          </Dropdown>
        </Fragment>
      )}

      {isOnTenderMovements && (
        <Fragment>
          <Dropdown
            overlay={messageMenu}
            trigger={['click']}
            placement="bottomRight">
            <Button
              type={'primary'}
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '180px',
                width: '168px',
              }}>
              <span>
                <IconButton icon={['far', 'envelope']} backgroundOff />
                {intl.formatMessage({ id: 'correlation-messages' })} (
                {draftCount})
                <DownOutlined style={{ marginLeft: 8, fontSize: '12px' }} />
              </span>
            </Button>
          </Dropdown>
          {/* DISABLED BY JIRA: TV1-176 disable all print features */}
          {/*<div*/}
          {/*  css={styles.buttonDiv}*/}
          {/*  onClick={handleMovementsToPrint}*/}
          {/*  key="printPage">*/}
          {/*  <IconButton*/}
          {/*    iconStyle="far"*/}
          {/*    icon="print"*/}
          {/*    customStyle={{*/}
          {/*      fontSize: '20px',*/}
          {/*      transition: '0.3s',*/}
          {/*    }}*/}
          {/*    backgroundOff*/}
          {/*  />*/}
          {/*  {intl.formatMessage({ id: 'print-page' })}*/}
          {/*</div>*/}
        </Fragment>
      )}
      {isOnTenderQA && (
        <Fragment>
          <Dropdown
            overlay={messageMenu}
            trigger={['click']}
            placement="bottomRight">
            <Button
              type={'primary'}
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '170px',
                width: '168px',
              }}>
              <span>
                <IconButton icon={['far', 'envelope']} backgroundOff />
                {intl.formatMessage({ id: 'correlation-messages' })} (
                {draftCount})
                <DownOutlined style={{ marginLeft: 8, fontSize: '12px' }} />
              </span>
            </Button>
          </Dropdown>
        </Fragment>
      )}
      {!isOnTenderDocuments && (
        <Fragment></Fragment>
        // <Fragment>
        //   <Dropdown overlay={moreMenu} trigger={['click']}>
        //     <div css={styles.buttonDiv}>
        //       <IconButton
        //         iconStyle="far"
        //         icon="ellipsis-h"
        //         customStyle={styles.iconsHeader}
        //         backgroundOff
        //       />
        //       <p css={{ marginBottom: 0, marginLeft: 2 }}>
        //         {intl.formatMessage({ id: 'more' })}
        //       </p>
        //     </div>
        //   </Dropdown>
        // </Fragment>
      )}

      <InviteModal
        tenderId={tenderId}
        visible={showInviteModal}
        onCancel={() => handleTenderFolderModalOpen(false, 'invite-modal')}
      />

      <CorrelationLetterModal
        visible={visibleCorrectionLetter}
        onCancel={() => {
          setVisibleCorrectionLetter(false);
          setSelectedDraft(null);
          if (isOnTenderQA || isOnTenderMovements) {
            refetchDrafts();
          }
        }}
        projectId={projectId!}
        tenderId={Number(tenderId)}
        tenderRegData={tenderData}
        isInQaTab={!isCorrectionMode && (isOnTenderQA || isOnTenderMovements)}
        instantMessageDraft={selectedDraft}
        isWinningLetter={!isCorrectionMode && isOnTenderOffers}
      />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ExclamationCircleOutlined
              style={{
                color: '#faad14',
                fontSize: '22px',
                marginRight: '10px',
              }}
            />
            <span>
              {intl.formatMessage({ id: 'invite-vendors-disclaimer-head' })}
            </span>
          </div>
        }
        visible={showDisclaimerModal}
        onOk={() => {
          setShowDisclaimerModal(false);
          setShowInviteModal(true);
        }}
        onCancel={() => setShowDisclaimerModal(false)}
        okText={intl.formatMessage({ id: 'ok' }) || 'OK'}
        cancelText={intl.formatMessage({ id: 'cancel' }) || 'Cancel'}
        centered
        destroyOnClose
        maskClosable={false}>
        <p
          style={{
            fontSize: '16px',
            marginBottom: '20px',
            textAlign: 'center',
          }}>
          {intl.formatMessage({ id: 'invite-vendors-disclaimer-body' })}
        </p>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ExclamationCircleOutlined
              style={{
                color: 'red',
                fontSize: '22px',
                marginRight: '10px',
              }}
            />
            <span>{intl.formatMessage({ id: 'Warning' })}</span>
          </div>
        }
        visible={showStopDisclaimerModal}
        onOk={() => {
          setShowStopDisclaimerModal(false);
          // action
        }}
        onCancel={() => setShowStopDisclaimerModal(false)}
        okText={intl.formatMessage({ id: 'ok' })}
        cancelText={intl.formatMessage({ id: 'cancel' })}
        centered
        destroyOnClose
        maskClosable={false}>
        <p
          style={{
            fontSize: '16px',
            marginBottom: '20px',
            textAlign: 'center',
          }}>
          {intl.formatMessage({ id: 'tender-warning-message-stop' })}
        </p>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ExclamationCircleOutlined
              style={{
                color: '#faad14',
                fontSize: '22px',
                marginRight: '10px',
              }}
            />
            <span>
              {intl.formatMessage({
                id: 'secondary-keys-enable-confirm-title',
              })}
            </span>
          </div>
        }
        visible={showActivateSecondaryKeysModal}
        onOk={handleActivateSecondaryKeys}
        onCancel={() => setShowActivateSecondaryKeysModal(false)}
        okText={intl.formatMessage({ id: 'secondary-keys-enable-confirm-ok' })}
        cancelText={intl.formatMessage({ id: 'cancel' })}
        okButtonProps={{ loading: isActivatingSecondaryKeys }}
        centered
        destroyOnClose
        maskClosable={false}>
        <p
          style={{
            fontSize: '16px',
            marginBottom: '20px',
            textAlign: 'center',
          }}>
          {intl.formatMessage({ id: 'secondary-keys-enable-confirm-body' })}
        </p>
      </Modal>
    </Fragment>
  );
};

export default TenderActions;
